import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';
import { HTTP_STATUS } from '../api/constants';

// Type definitions
export const DrawType = {
  Random: 0,
  ScoreBased: 1,
  FirstComeFirstServed: 2
};

export const DeliveryMethod = {
  Physical: 0,
  Digital: 1,
  Coupon: 2
};

export const DepartmentType = {
  All: 0,
  Specific: 1,
  New: 2,
  Senior: 3
};

export const DrawStatus = {
  Draft: 0,
  Active: 1,
  Upcoming: 2,
  Completed: 3,
  Expired: 4,
  Cancelled: 5
};

// API Response types
export const ResponseStatus = {
  OK: 0,
  CREATED: 1,
  ERROR: 2,
  FORBIDDEN: 3,
  UNAUTHORIZED: 4,
  INVALID: 5,
  NOT_FOUND: 6,
  NO_CONTENT: 7,
  CONFLICT: 8,
  CRITICAL_ERROR: 9,
  UNAVAILABLE: 10
};

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const drawApi = createApi({
  reducerPath: 'drawApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Draw', 'Department', 'Tenant'],
  endpoints: (builder) => ({
    // Get draw list with optional filtering
    getDraws: builder.query({
      query: (params = {}) => ({
        url: '/draw/list',
        method: 'GET',
        params
      }),
      transformResponse: (response) => {
        if (!response) {
          return {
            value: {
              draws: [],
              totalCount: 0
            },
            isSuccess: false,
            status: ResponseStatus.ERROR,
            errors: [],
            validationErrors: [],
            successMessage: '',
            correlationId: '',
            location: ''
          };
        }

        return {
          value: {
            draws: response.value?.draws || [],
            totalCount: response.value?.totalCount || 0
          },
          isSuccess: response.isSuccess,
          status: response.status || 0,
          successMessage: response.successMessage || '',
          correlationId: response.correlationId || '',
          location: response.location || '',
          errors: response.errors || [],
          validationErrors: response.validationErrors || []
        };
      },
      providesTags: (result) => 
        result?.value?.draws
          ? [
              ...result.value.draws.map(({ id }) => ({ type: 'Draw', id })),
              { type: 'Draw', id: 'LIST' }
            ]
          : [{ type: 'Draw', id: 'LIST' }]
    }),

    // Get draws specific to a tenant
    getDrawsByTenant: builder.query({
      query: (params = {}) => {
        // Clean and prepare query parameters
        const queryParams = {
          page: params.page || 0,
          pageSize: params.pageSize || 8,
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.departmentId && { departmentId: params.departmentId }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          // Only include sort parameters if they differ from server defaults
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder })
        };

        console.log('🔍 Draw API Query Params (keyword):', queryParams);

        return {
          url: '/draw/list',
          method: 'GET',
          params: queryParams
        };
      },
      transformResponse: (response) => {
        if (!response) {
          return {
            value: {
              draws: [],
              totalCount: 0
            },
            isSuccess: false,
            status: ResponseStatus.ERROR,
            errors: [],
            validationErrors: [],
            successMessage: '',
            correlationId: '',
            location: ''
          };
        }

        return {
          value: {
            draws: response.value?.draws || [],
            totalCount: response.value?.totalCount || 0
          },
          isSuccess: response.isSuccess,
          status: response.status || 0,
          successMessage: response.successMessage || '',
          correlationId: response.correlationId || '',
          location: response.location || '',
          errors: response.errors || [],
          validationErrors: response.validationErrors || []
        };
      },
      providesTags: (result) => 
        result?.value?.draws
          ? [
              ...result.value.draws.map(({ id }) => ({ type: 'Draw', id })),
              { type: 'Draw', id: 'TENANT_LIST' }
            ]
          : [{ type: 'Draw', id: 'TENANT_LIST' }]
    }),

    // Get single draw details
    getDrawById: builder.query({
      query: (drawId) => ({
        url: `/draw/${drawId}`,
        method: 'GET'
      }),
      transformResponse: (response) => response,
      providesTags: (result, error, id) => [{ type: 'Draw', id }]
    }),

    // Create draw
    createDraw: builder.mutation({
      query: (data) => ({
        url: '/draw/create',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: 'Draw', id: 'LIST' }]
    }),

    // Create tenant-specific draw
    createTenantDraw: builder.mutation({
      query: (data) => ({
        url: '/draw/create',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: 'Draw', id: 'LIST' },
        { type: 'Draw', id: 'TENANT_LIST' }
      ]
    }),

    // Update draw
    updateDraw: builder.mutation({
      query: (data) => ({
        url: '/draw/update',
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Draw', id },
        { type: 'Draw', id: 'LIST' }
      ]
    }),

    // Add draw media
    addDrawMedia: builder.mutation({
      query: (formData) => {
        console.log('addDrawMedia called with formData:', formData);
        // FormData içeriğini kontrol et
        if (formData instanceof FormData) {
          console.log('FormData entries:');
          for (let pair of formData.entries()) {
            console.log(pair[0], pair[1]);
          }
        }
        return {
          url: '/draw/media',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      transformResponse: (response) => {
        console.log('addDrawMedia response:', response);
        if (!response) {
          return {
            value: '',
            isSuccess: false,
            status: ResponseStatus.ERROR,
            errors: [],
            validationErrors: [],
            successMessage: '',
            correlationId: '',
            location: ''
          };
        }
        
        return {
          value: response.value,
          isSuccess: response.isSuccess,
          status: response.status || 0,
          successMessage: response.successMessage || '',
          correlationId: response.correlationId || '',
          location: response.location || '',
          errors: response.errors || [],
          validationErrors: response.validationErrors || []
        };
      },
      invalidatesTags: ['Draw']
    }),

    // Get departments for filtering
    getDepartments: builder.query({
      query: () => ({
        url: '/department/list',
        method: 'GET'
      }),
      transformResponse: (response) => response.value,
      providesTags: ['Department']
    }),

    // Get tenants for filtering
    getTenants: builder.query({
      query: () => ({
        url: '/tenant/list',
        method: 'GET'
      }),
      transformResponse: (response) => response.value,
      providesTags: ['Tenant']
    })
  })
});

export const {
  useGetDrawsQuery,
  useGetDrawsByTenantQuery,
  useGetDrawByIdQuery,
  useCreateDrawMutation,
  useCreateTenantDrawMutation,
  useUpdateDrawMutation,
  useAddDrawMediaMutation,
  useGetDepartmentsQuery,
  useGetTenantsQuery
} = drawApi;

// Aliases for backward compatibility
export const useGetDrawListQuery = useGetDrawsQuery;
export const useUploadDrawMediaMutation = useAddDrawMediaMutation;

export const {
  reducer: drawReducer,
  middleware: drawMiddleware,
  reducerPath: drawReducerPath
} = drawApi;
